// [목적] OAuth 4종(Kakao/Naver/Google/Apple) 버튼 렌더링 dumb 컴포넌트
//         LoginMobileLayout / LoginDesktopLayout 두 레이아웃이 동일한 버튼 구현을 공유한다.
// [이유] LoginPage.tsx 에 인라인으로 정의된 SOCIALS 배열·버튼 렌더 로직을 추출(T-FE-01).
//         dumb 컴포넌트 구조로 설계하여 onClick 핸들러를 부모가 보유 — OAuth 흐름 회귀 위험(R-8) 최소화.
//         2026-05-16 reauth-redesign — 디자인 원본의 SOCIAL 순서 (Kakao→Apple→Google→Naver) 반영.
//         Apple disabled+Tooltip 처리는 보존하되 disabledProviders 미지정 시 활성.
// [사이드 이팩트]
//   - SOCIALS 순서: Kakao(추천) → Naver → Google → Apple (Spec §4-5).
//   - 색상은 AUTH_COLORS 토큰 경유 (R-6, AC-6).
//   - Apple 버튼이 disabledProviders 에 포함되면 disabled + MUI Tooltip "준비 중".
//   - `:focus-visible` 인디케이터: outline brandBlue 2px, offset 2px (AC-5).
// [수정 시 고려사항]
//   - OAuth 공식 브랜드 가이드라인 준수 의무 (R-2, AC-6):
//       각 IdP 가이드 변경 시 AUTH_COLORS 토큰을 갱신할 것 (이 파일 직접 수정 금지).
//   - provider 타입 확장(Apple 활성화 등)은 Provider 유니언 타입 + SOCIALS 배열만 수정.
//   - fullWidth prop: 모바일은 true, 데스크톱 우측 폼 패널도 true (기본값 true).
//   - 버튼 라벨 변경 시 aria-label 도 동기화 필수 (스크린리더 기준).

import { Box, ButtonBase, Tooltip } from '@mui/material';

import { AUTH_BORDER, AUTH_COLORS, AUTH_SPACING } from './tokens.js';

// ─── 타입 ──────────────────────────────────────────────────────────────────

// [수정: 디자인 개편] Apple을 비활성화가 아니라 유니온에서 완전히 제거 — 목업은 3개(카카오/구글/네이버)만 보여줌.
export type OAuthProvider = 'kakao' | 'naver' | 'google';

export interface OAuthButtonStackProps {
  /** 버튼 클릭 시 호출. provider 를 부모에게 위임하여 OAuth 흐름을 보호한다. */
  onProviderClick: (provider: OAuthProvider) => void;
  /** disabled 처리할 provider 목록. Apple 은 BE 미구현 시 ['apple'] 전달. */
  disabledProviders?: OAuthProvider[];
  /** 버튼 폭 100% 여부. 기본 true. */
  fullWidth?: boolean;
}

// ─── 내부 데이터 ──────────────────────────────────────────────────────────

interface SocialEntry {
  id: OAuthProvider;
  label: string;
  ariaLabel: string;
  bg: string;
  color: string;
  icon: string;
  border?: boolean;
  recommend?: boolean;
}

// [목적] 4개 소셜 버튼 정의 (디자인 원본 순서: Kakao → Apple → Google → Naver)
// [이유] docs/플리오랩/gangwonfolio-web.html LoginScreenA 의 button.map 배열 1:1 매핑
// [사이드 이팩트] 순서 변경 시 모바일/데스크톱 동시 영향. disabledProviders 우선순위 보존
// [수정 시 고려사항] Apple 'icon' 은 빈 문자열 — 디자인 원본대로. WCAG: aria-label 보강 필수
const SOCIALS: ReadonlyArray<SocialEntry> = [
  {
    id: 'kakao',
    label: '카카오로 3초 만에 시작',
    ariaLabel: '카카오로 로그인',
    bg: AUTH_COLORS.kakaoBg,
    color: AUTH_COLORS.kakaoText,
    icon: '💬',
    recommend: true,
  },
  {
    id: 'google',
    label: 'Google로 시작하기',
    ariaLabel: 'Google로 로그인',
    bg: AUTH_COLORS.googleBg,
    color: AUTH_COLORS.googleText,
    icon: 'G',
    border: true,
  },
  {
    id: 'naver',
    label: '네이버로 시작하기',
    ariaLabel: '네이버로 로그인',
    bg: AUTH_COLORS.naverBg,
    color: AUTH_COLORS.naverText,
    icon: 'N',
  },
];

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────

/**
 * OAuth 4종 버튼 스택.
 * onClick 핸들러를 부모에게 위임하는 dumb 컴포넌트.
 * Apple 은 disabledProviders=['apple'] 전달 시 disabled + Tooltip "준비 중".
 */
export function OAuthButtonStack({
  onProviderClick,
  disabledProviders = [],
  fullWidth = true,
}: OAuthButtonStackProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: AUTH_SPACING.stackGap,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {SOCIALS.map((b) => {
        const isDisabled = disabledProviders.includes(b.id);
        const button = (
          <ButtonBase
            key={b.id}
            disabled={isDisabled}
            onClick={() => !isDisabled && onProviderClick(b.id)}
            aria-label={isDisabled ? `${b.ariaLabel} (준비 중)` : b.ariaLabel}
            aria-disabled={isDisabled}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
              // [수정: 디자인 개편] 3개 버튼 높이를 48px로 통일(기존에는 padding으로만 높이가 결정되어 미묘하게 달랐음).
              height: '48px',
              bgcolor: isDisabled ? undefined : b.bg,
              color: isDisabled ? AUTH_COLORS.disabledText : b.color,
              border: b.border
                ? `1.5px solid ${AUTH_COLORS.googleBorder}`
                : isDisabled
                  ? `1.5px solid ${AUTH_COLORS.dividerBase}`
                  : 'none',
              borderRadius: AUTH_BORDER.buttonRadius,
              p: '14px',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '-0.2px',
              // Kakao 추천 배지 그림자
              boxShadow:
                b.recommend && !isDisabled ? `0 4px 12px ${AUTH_COLORS.kakaoShadow}` : 'none',
              // disabled 시 배경 반투명
              opacity: isDisabled ? 0.45 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              // 포커스 링 (AC-5, WCAG 2.1 AA)
              '&:focus-visible': {
                outline: `${AUTH_BORDER.focusRingWidth} solid ${AUTH_COLORS.brandBlue}`,
                outlineOffset: AUTH_BORDER.focusRingOffset,
              },
            }}
          >
            {/* 추천 배지 */}
            {b.recommend && !isDisabled && (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  top: -7,
                  right: 14,
                  bgcolor: AUTH_COLORS.recommendBadgeBg,
                  color: AUTH_COLORS.recommendBadgeText,
                  fontSize: 9,
                  fontWeight: 700,
                  borderRadius: '5px',
                  px: '7px',
                  py: '2px',
                  letterSpacing: '0.3px',
                }}
              >
                추천
              </Box>
            )}
            {/* 아이콘 */}
            <Box
              component="span"
              aria-hidden="true"
              sx={{ fontSize: 15, fontWeight: 800, width: 18, textAlign: 'center' }}
            >
              {b.icon}
            </Box>
            {/* 라벨 */}
            <Box component="span">{b.label}</Box>
          </ButtonBase>
        );

        // Apple disabled → Tooltip "준비 중"
        if (isDisabled) {
          return (
            <Tooltip key={b.id} title="준비 중" placement="top" arrow>
              {/* Tooltip 은 disabled ButtonBase 에 직접 작동하지 않으므로 span 래핑 */}
              <Box component="span" sx={{ display: 'block', width: '100%' }}>
                {button}
              </Box>
            </Tooltip>
          );
        }

        return button;
      })}
    </Box>
  );
}
